import { getResumeUploadId } from "@/lib/domain/resume-upload";

export const ONBOARDING_FORCE_QUERY_PARAM = "onboarding";
export const ONBOARDING_FORCE_QUERY_VALUE = "1";
export const ONBOARDING_SIGNUP_FORCE_WINDOW_MS = 30 * 60 * 1000;
export const ONBOARDING_DEBUG_FORCE_QUERY_PARAM = "forceOnboarding";

const ONBOARDING_PRODUCT_PATH_PREFIXES = [
  "/home",
  "/dashboard",
  "/builder",
  "/find",
  "/apply",
  "/interview",
  "/create",
  "/settings",
  "/linkedin",
  "/parser",
] as const;

function normalizePathname(pathname: string): string {
  try {
    return new URL(pathname, "https://landfast.app").pathname;
  } catch {
    return pathname.split("?")[0]?.split("#")[0] ?? pathname;
  }
}

export function shouldForceOnboardingFromSearch(search: string): boolean {
  try {
    return (
      new URLSearchParams(search).get(ONBOARDING_FORCE_QUERY_PARAM) ===
      ONBOARDING_FORCE_QUERY_VALUE
    );
  } catch {
    return false;
  }
}

export function shouldDebugForceOnboardingFromSearch(search: string): boolean {
  try {
    return (
      new URLSearchParams(search).get(ONBOARDING_DEBUG_FORCE_QUERY_PARAM) ===
      ONBOARDING_FORCE_QUERY_VALUE
    );
  } catch {
    return false;
  }
}

export function removeOnboardingForceParamFromHref(href: string): string {
  try {
    const url = new URL(href);
    url.searchParams.delete(ONBOARDING_FORCE_QUERY_PARAM);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return href;
  }
}

export function isRecentAccountCreation(
  createdAt: Date | number | string | null | undefined,
  now = Date.now()
): boolean {
  if (!createdAt) return false;

  const createdAtMs =
    createdAt instanceof Date
      ? createdAt.getTime()
      : typeof createdAt === "number"
        ? createdAt
        : new Date(createdAt).getTime();

  if (!Number.isFinite(createdAtMs) || createdAtMs > now) return false;

  return now - createdAtMs <= ONBOARDING_SIGNUP_FORCE_WINDOW_MS;
}

export function canOpenOnboardingOnPathname(
  pathname: string | null | undefined
): boolean {
  if (!pathname) return false;

  try {
    const url = new URL(pathname, "https://landfast.app");
    if (url.pathname === "/builder" && getResumeUploadId(url.search)) return false;
  } catch {
    return false;
  }

  const normalizedPathname = normalizePathname(pathname);
  return ONBOARDING_PRODUCT_PATH_PREFIXES.some(
    (prefix) =>
      normalizedPathname === prefix ||
      normalizedPathname.startsWith(`${prefix}/`)
  );
}

export function shouldOpenOnboardingForRoute({
  pathname,
  alreadyDone,
}: {
  pathname: string | null | undefined;
  alreadyDone: boolean;
}): boolean {
  return !alreadyDone && canOpenOnboardingOnPathname(pathname);
}
